/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { isSafari } from '../utils/browserUtils';
import { useIsConfluenceModule } from '../utils/useIsConfluenceModule';

// This overrides Confluence webpack styling that affects chart components in confluence modules
const chartStyle = css({
	// Styling override for UI Kit chart y-axis label
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-element="layout"] div[data-axis-type="axis-left"]': {
		minHeight: token('space.0'),
		height: token('space.0'),
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
	// Styling override for UI Kit chart x-axis label
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-element="layout"] div[data-axis-type="axis-bottom"]': {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
	// Styling override for UI Kit chart title
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-element="layout"] h2': {
		font: token('font.heading.small'),
		color: token('color.text.subtle'),
	},
	// Styling override for UI Kit chart subtitle
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-element="layout"] h3': {
		font: token('font.body'),
		color: token('color.text.subtle'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-element="layout"] div[data-element="subtitle"]': {
		paddingBottom: token('space.200'),
	},
	// Styling override for UI Kit chart legend
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-element="chart"] > div > div > div[role="presentation"] > div': {
		marginLeft: token('space.0'),
		marginRight: token('space.0'),
		font: token('font.heading.xxsmall'),
		color: token('color.text.subtlest'),
	},
});

// Styling override in Safari for UI Kit chart y-axis (axis-left) label that gets overlapped by chart plot.
// (See https://ecosystem-platform.atlassian.net/browse/EXT-3078)
const safariChartStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-element="chart"] > div > div': {
		gridTemplateColumns: 'max-content auto',
	},
});

export const withConfluenceStyleWrapper = (
	ChartComponent: (props: Parameters<RenderFn>[0]) => React.JSX.Element,
) => {
	return function ConfluenceStyleWrapper(props: Parameters<RenderFn>[0]): JSX.Element {
		const isConfluenceModule = useIsConfluenceModule();

		return isConfluenceModule ? (
			<div css={[chartStyle, isSafari() && safariChartStyle]}>
				<ChartComponent {...props} />
			</div>
		) : (
			<ChartComponent {...props} />
		);
	};
};
